<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Yêu cầu bổ sung ngân sách'">
        <template v-slot:toolbar>
          <b-button
            v-if="checkPermission('BUDGET_INSERT')"
            variant="primary"
            size="sm"
            @click="createRequest"
          >
            <i class="fas fa-plus"></i> Tạo yêu cầu
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Filters row 1 -->
          <b-row class="mb-3">
            <b-col cols="12" md="2">
              <date-picker
                v-model="filterPeriod"
                :config="dpMonth"
                class="form-control-sm"
                placeholder="Kỳ (MM/YYYY)"
              />
            </b-col>
            <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <BDropdownCustom
                v-model="filterDepartmentId"
                :options="departmentDropdownOptions"
                :placeholder="'phòng ban'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <BDropdownCustom
                v-model="filterCostItemId"
                :options="costItemDropdownOptions"
                :placeholder="'khoản mục'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="12" md="4" class="mt-2 mt-md-0">
              <BDropdownCustom
                v-model="filterStoreId"
                :options="storeDropdownOptions"
                :placeholder="'cửa hàng'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
          </b-row>

          <!-- Filters row 2 -->
          <b-row class="mb-3">
            <b-col cols="12" md="2">
              <b-form-select
                v-model="filterCompanyId"
                :options="companyOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <BDropdownCustom
                v-model="filterCostGroupId"
                :options="costGroupDropdownOptions"
                :placeholder="'nhóm phí'"
                :matchKey="['name']"
                :searchable="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <b-form-select
                v-model="filterStatus"
                :options="statusOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="12" md="4" class="mt-2 mt-md-0">
              <Autosuggest
                :model.sync="employeeModel"
                :suggestions="employeeSuggestions"
                :limit="suggestLimit"
                suggestionName="suggestionName"
                placeholder="người tạo"
                @select="onSelectedEmployee"
                @change="onSearchEmployeeChange"
              />
            </b-col>
          </b-row>

          <!-- Filters row 3 -->
          <b-row class="mb-3">
             <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <b-form-select
                v-model="filterSortBy"
                :options="sortByOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="12" md="3" class="d-flex align-items-center">
              <b-form-checkbox v-model="filterMyApproval">
                Cần tôi duyệt
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col cols="12" md="2">
              <b-button variant="primary" size="sm" @click="onFilter">
                <i class="fas fa-search"></i> Lọc
              </b-button>
            </b-col>
          </b-row>

          <!-- Thanh thao tác hàng loạt: hiện khi đã chọn >= 1 phiếu -->
          <b-row v-if="selectedItems.length" class="mb-3 align-items-center">
            <b-col cols="12" class="d-flex align-items-center flex-wrap">
              <span class="mr-3 font-weight-bold">
                Đã chọn {{ selectedItems.length }} phiếu
              </span>
              <b-button
                variant="success"
                size="sm"
                class="mr-2"
                @click="openBulkApprove"
              >
                <i class="fas fa-check"></i> Duyệt hàng loạt
              </b-button>
              <b-button variant="outline-secondary" size="sm" @click="clearSelection">
                Bỏ chọn
              </b-button>
            </b-col>
          </b-row>

          <!-- Table -->
          <b-table
            :items="items"
            :fields="fields"
            bordered
            hover
            :busy="onLoading"
            show-empty
            empty-text="Không có dữ liệu"
          >
            <template #head(checkbox)>
              <b-form-checkbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                :disabled="!selectableItems.length"
                @change="toggleSelectAll"
              />
            </template>
            <template #cell(checkbox)="row">
              <b-form-checkbox
                v-if="isSelectable(row.item)"
                v-model="row.item.selected"
              />
              <span
                v-else
                v-b-tooltip.hover
                title="Phiếu không ở bước duyệt của bạn"
                style="cursor: not-allowed"
              >
                <b-form-checkbox disabled />
              </span>
            </template>
            <template #cell(requestId)="{ item }">
              <a :href="detailHref(item)" @click="onIdClick($event, item)">
                <strong>#{{ item.id }}</strong>
              </a>
            </template>
            <template #cell(period)="{ item }">
              <div>{{ formatPeriodColumn(item) }}</div>
              <small
                v-if="Array.isArray(item.periods) && item.periods.length > 1"
                class="text-muted"
              >
                ({{ item.periods.length }} kỳ)
              </small>
            </template>
            <template #cell(info)="{ item }">
              <div>
                <div><span class="font-weight-bold">Doanh nghiệp: </span>{{ item.companyName || '—' }}</div>
                <div><span class="font-weight-bold">Phòng ban: </span>{{ item.departmentName || '—' }}</div>
                <div><span class="font-weight-bold">Khoản mục: </span>{{ item.costItemName || '—' }}</div>
                <div><span class="font-weight-bold">Cửa hàng: </span>{{ item.storeName || 'Cấp phòng ban' }}</div>
              </div>
            </template>
            <template #cell(budgetLineId)="{ item }">
              <router-link
                v-if="item.budgetLineId"
                :to="{ name: 'detail-budget-line', query: { id: item.budgetLineId } }"
                target="_blank"
                class="text-primary small"
              >
                #{{ item.budgetLineId }}
                <i class="fas fa-external-link-alt" style="font-size: 10px"></i>
              </router-link>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(totalAmount)="{ item }">
              <div class="text-right">
                <strong>{{ convertPrice(getTotalAmount(item)) }} đ</strong>
                <small
                  v-if="Array.isArray(item.periods) && item.periods.length"
                  class="text-muted d-block"
                >
                  {{ item.periods.length }} kỳ
                </small>
              </div>
            </template>
            <template #cell(nextApprovalStep)="{ item, value }">
              <span style="cursor: pointer" @click="openApprovalWorkflow(item)">
                <ApprovalStepBadge :step="value" />
                <small class="text-muted" v-if="item.nextApprovalStep?.updatedAt" v-b-tooltip.hover title="Ngày cập nhật trạng thái"> {{ formatDate(item.nextApprovalStep?.updatedAt, CLIENT_DATE_TIME_NO_SECONDS) }} </small>
              </span>
            </template>
            <template #cell(status)="{ item }">
              <b-badge
                :variant="statusVariant(item.status)"
                style="cursor: pointer"
                @click="openApprovalWorkflow(item)"
              >
                {{ statusName(item.status) }}
              </b-badge>
            </template>
            <template #cell(createdAt)="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <b-dropdown size="sm" id="dropdown-left" no-caret right>
                  <template #button-content>
                    <i class="flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item @click="viewDetail(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-sheet icon-size-sm"></i>
                      &nbsp; Chi tiết
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="showHistory(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-time icon-size-sm"></i>
                      &nbsp; Lịch sử
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canApprove && isApprovable(row.item.status)"
                    @click="openApprovalModal(row.item, WORKFLOW_APPROVAL_STEP_STATUS.APPROVED)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-checkmark icon-size-sm"></i>
                      &nbsp; Duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canApprove && isApprovable(row.item.status)"
                    @click="openApprovalModal(row.item, WORKFLOW_APPROVAL_STEP_STATUS.REJECTED)"
                  >
                    <span style="color: #f64e60; font-size: 10px">
                      <i class="flaticon2-cancel icon-size-sm"></i>
                      &nbsp; Không duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canUndo"
                    @click="undoItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="fas fa-undo icon-size-sm"></i>
                      &nbsp; Hoàn duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canEdit"
                    @click="editItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-pen icon-size-sm"></i>
                      &nbsp; Sửa
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.status === BUDGET_LINE_REQUEST_STATUS.DRAFT"
                    @click="confirmDelete(row.item)"
                  >
                    <span style="color: #f64e60; font-size: 10px">
                      <i class="flaticon2-trash icon-size-sm"></i>
                      &nbsp; Xóa
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <!-- Modal duyệt / không duyệt yêu cầu NS -->
          <b-modal
            ref="approvalModal"
            :title="approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED ? 'Duyệt yêu cầu bổ sung NS' : 'Không duyệt yêu cầu bổ sung NS'"
            @ok="confirmApproval"
            ok-title="Xác nhận"
            cancel-title="Đóng"
            :ok-variant="approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED ? 'success' : 'danger'"
          >
            <b-form-group>
              <template #label>
                <span v-if="approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED">Ghi chú duyệt</span>
                <span v-else>Lý do không duyệt <span class="text-danger">*</span></span>
              </template>
              <b-form-textarea
                v-model="approvalForm.note"
                rows="3"
                :placeholder="approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED ? 'Ghi chú (nếu có)' : 'Nhập lý do không duyệt'"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal hoàn duyệt yêu cầu NS -->
          <b-modal
            ref="undoModal"
            title="Hoàn duyệt yêu cầu bổ sung NS"
            @ok="confirmUndo"
            ok-variant="warning"
            ok-title="Hoàn duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="undoForm.note"
                rows="2"
                placeholder="Nhập ghi chú hoàn duyệt"
              />
            </b-form-group>
          </b-modal>

          <BudgetLineRequestApprovalModal
            ref="requestApprovalModal"
            :requestId="approvalModalRequestId"
          />

          <PurchaseRequestHistoryModal
            ref="historyModal"
            :histories="historyItems"
          />

          <BudgetLineRequestQuickViewModal ref="quickViewModal" />

          <!-- Modal xác nhận duyệt hàng loạt -->
          <b-modal
            ref="bulkApproveModal"
            title="Duyệt hàng loạt"
            ok-title="Xác nhận"
            cancel-title="Đóng"
            ok-variant="success"
            :ok-disabled="bulkSubmitting"
            @ok.prevent="confirmBulkApprove"
          >
            <p class="mb-2">
              Sẽ duyệt <strong>{{ bulkForm.eligible.length }}</strong> phiếu.
            </p>
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="bulkForm.note"
                rows="3"
                placeholder="Nhập ghi chú duyệt (áp dụng cho tất cả phiếu)"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal kết quả xử lý hàng loạt (tái dùng của Phiếu PR) -->
          <PurchaseRequestBulkResultModal
            ref="bulkResultModal"
            @closed="onBulkResultClosed"
          />

          <b-row class="mt-3 align-items-center">
            <b-col cols="12" md="4">
              Tổng số: <strong>{{ totalRow }}</strong>
            </b-col>
            <b-col cols="12" md="8" class="d-flex justify-content-end">
              <BPaginationNavCustom
                :currentPage="page"
                :numberOfPages="numberOfPage || 1"
                :totalPage="numberOfPage"
                :linkParams="paginationLinkParams"
                @page-change="fetchData"
              />
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission, getData } from '@/utils/saveDataToLocal';
import { makeToastSuccess, makeToastFaile, convertPrice, handleError, xoa_dau } from '@/utils/common';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ApprovalStepBadge from '@/view/base/bootstrap/ApprovalStepBadge.vue';
import BudgetLineRequestApprovalModal from './components/BudgetLineRequestApprovalModal.vue';
import BudgetLineRequestQuickViewModal from './components/BudgetLineRequestQuickViewModal.vue';
import PurchaseRequestHistoryModal from '../purchase-requests/components/PurchaseRequestHistoryModal.vue';
import PurchaseRequestBulkResultModal from '../purchase-requests/components/PurchaseRequestBulkResultModal.vue';
import moment from 'moment';
import {
  BUDGET_LINE_REQUEST_STATUS,
  BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import { BUDGET_LINE_REQUEST_STATUS_NAME, HISTORY_ACTION_TYPE_CODE } from '@/utils/enum-name';
import { CLIENT_DATE_TIME_NO_SECONDS } from '@/utils/date';

export default {
  components: { KTCodePreview, BDropdownCustom, BPaginationNavCustom, Autosuggest, ApprovalStepBadge, BudgetLineRequestApprovalModal, BudgetLineRequestQuickViewModal, PurchaseRequestHistoryModal, PurchaseRequestBulkResultModal },
  data() {
    return {
      BUDGET_LINE_REQUEST_STATUS,
      WORKFLOW_APPROVAL_STEP_STATUS,
      CLIENT_DATE_TIME_NO_SECONDS,
      approvalAction: WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
      approvalForm: { id: null, note: '' },
      undoForm: { id: null, note: '' },
      // Duyệt hàng loạt
      bulkForm: { note: '', eligible: [] },
      bulkSubmitting: false,
      approvalModalRequestId: null,
      historyItems: [],
      items: [],
      onLoading: false,
      page: 1,
      numberOfPage: 1,
      totalRow: 0,
      filterPeriod: '',
      filterDepartmentId: [],
      filterCostItemId: [],
      filterStoreId: [],
      filterCompanyId: null,
      filterCostGroupId: [],
      filterStatus: null,
      filterCreatedBy: null,
      filterMyApproval: false,
      filterSortBy: 'status',
      companyId: null,
      employeeModel: '',
      suggestLimit: 50,
      employeeSource: [],
      employeeSuggestions: [],
      departmentDropdownOptions: [],
      costItemDropdownOptions: [],
      storeDropdownOptions: [],
      costGroupDropdownOptions: [],
      companyOptions: [{ value: null, text: '-- Doanh nghiệp --' }],
      statusOptions: [
        { value: null, text: '-- Trạng thái --' },
        { value: BUDGET_LINE_REQUEST_STATUS.DRAFT, text: BUDGET_LINE_REQUEST_STATUS_NAME[BUDGET_LINE_REQUEST_STATUS.DRAFT] },
        { value: BUDGET_LINE_REQUEST_STATUS.PENDING, text: BUDGET_LINE_REQUEST_STATUS_NAME[BUDGET_LINE_REQUEST_STATUS.PENDING] },
        { value: BUDGET_LINE_REQUEST_STATUS.BROWSING, text: BUDGET_LINE_REQUEST_STATUS_NAME[BUDGET_LINE_REQUEST_STATUS.BROWSING] },
        { value: BUDGET_LINE_REQUEST_STATUS.APPROVED, text: BUDGET_LINE_REQUEST_STATUS_NAME[BUDGET_LINE_REQUEST_STATUS.APPROVED] },
        { value: BUDGET_LINE_REQUEST_STATUS.REJECTED, text: BUDGET_LINE_REQUEST_STATUS_NAME[BUDGET_LINE_REQUEST_STATUS.REJECTED] },
      ],
      sortByOptions: [
        { value: null, text: '-- Sắp xếp --' },
        { value: 'status', text: 'Sắp xếp theo trạng thái' },
        { value: 'createdAt', text: 'Sắp xếp theo ngày tạo' },
      ],
      dpMonth: {
        format: 'MM/YYYY',
        useCurrent: false,
        showTodayButton: false,
        locale: 'vi',
        minDate: false,
      },
      fields: [
        { key: 'checkbox', label: '', thStyle: { width: '3%' }, class: 'text-center align-middle' },
        { key: 'requestId', label: 'ID phiếu', thStyle: { width: '6%' }, class: 'text-center' },
        { key: 'period', label: 'Kỳ', thStyle: { width: '8%' }, class: 'text-center' },
        { key: 'info', label: 'Thông tin', thStyle: { width: '24%' } },
        { key: 'totalAmount', label: 'Tổng số tiền', thStyle: { width: '12%' }, tdClass: 'text-right' },
        { key: 'budgetLineId', label: 'Dòng NS', thStyle: { width: '10%' }, class: 'text-center' },
        { key: 'nextApprovalStep', label: 'Bước duyệt', thStyle: { width: '9%' }, class: 'text-center' },
        { key: 'status', label: 'Trạng thái', thStyle: { width: '9%' }, class: 'text-center' },
        { key: 'createdByName', label: 'Người tạo', thStyle: { width: '9%' }, class: 'text-center' },
        { key: 'createdAt', label: 'Ngày tạo', thStyle: { width: '10%' }, class: 'text-center' },
        { key: 'actions', label: '', thStyle: { width: '5%' }, class: 'text-center' },
      ],
    };
  },
  computed: {
    paginationLinkParams() {
      return {
        period: this.filterPeriod || '',
        departmentId: this.filterDepartmentId.map((d) => d.id),
        costItemId: this.filterCostItemId.map((c) => c.id),
        storeId: this.filterStoreId.map((s) => s.id),
        companyId: this.filterCompanyId || '',
        costGroupId: this.filterCostGroupId.map((g) => g.id),
        status: this.filterStatus !== null ? this.filterStatus : '',
        createdBy: this.filterCreatedBy || '',
        myApproval: this.filterMyApproval ? 1 : '',
        sortBy: this.filterSortBy || '',
      };
    },
    // Phiếu có thể chọn để duyệt hàng loạt: đang ở bước duyệt của user
    selectableItems() {
      return this.items.filter((item) => this.isSelectable(item));
    },
    selectedItems() {
      return this.items.filter((item) => item.selected);
    },
    isAllSelected() {
      return (
        this.selectableItems.length > 0 &&
        this.selectableItems.every((item) => item.selected)
      );
    },
    isIndeterminate() {
      const selectedCount = this.selectableItems.filter((item) => item.selected).length;
      return selectedCount > 0 && selectedCount < this.selectableItems.length;
    },
  },
  created() {
    const user = getData('user');
    this.companyId = (user && user.companyId) || null;
    this.applyQueryFilters();
    this.fetchFilterOptions();
    this.fetchEmployees();
    this.fetchData(this.page);
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Yêu cầu bổ sung NS', route: '/budget/budget-line-requests' },
    ]);
  },
  methods: {
    checkPermission,
    applyQueryFilters() {
      const q = this.$route?.query || {};
      this.page = Number(q.page) || 1;
      this.filterStatus = q.status !== undefined && q.status !== '' ? q.status : null;
      this.filterSortBy = q.sortBy || 'status';
      this.filterCompanyId = q.companyId ? Number(q.companyId) : null;
      this.filterCreatedBy = q.createdBy ? Number(q.createdBy) : null;
      this.filterMyApproval = q.myApproval === '1' || q.myApproval === 1;
      this.filterPeriod = q.period ? String(q.period) : '';

      if (!this.filterCreatedBy) {
        this.employeeModel = '';
      }

      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);
      if (this.departmentDropdownOptions.length) {
        const ids = toIds(q.departmentId);
        this.filterDepartmentId = this.departmentDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.costItemDropdownOptions.length) {
        const ids = toIds(q.costItemId);
        this.filterCostItemId = this.costItemDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.storeDropdownOptions.length) {
        const ids = toIds(q.storeId);
        this.filterStoreId = this.storeDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.costGroupDropdownOptions.length) {
        const ids = toIds(q.costGroupId);
        this.filterCostGroupId = this.costGroupDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
    },
    isApprovable(status) {
      return (
        status === BUDGET_LINE_REQUEST_STATUS.PENDING ||
        status === BUDGET_LINE_REQUEST_STATUS.BROWSING
      );
    },
    openApprovalModal(item, action) {
      this.approvalAction = action;
      this.approvalForm = { id: item.id, note: '' };
      this.$refs.approvalModal.show();
    },
    undoItem(item) {
      this.undoForm = { id: item.id, note: '' };
      this.$refs.undoModal.show();
    },
    confirmUndo() {
      if (!this.undoForm?.id) return;
      ApiService.patch(`v2/budget-line-requests/${this.undoForm.id}/change-status`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.REFUND_APPROVED,
        note: this.undoForm.note || '',
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Hoàn duyệt thành công!');
          this.fetchData(this.page);
          this.$refs.undoModal.hide();
        })
        .catch((err) => handleError(err));
    },
    confirmApproval(evt) {
      evt.preventDefault();
      const id = this.approvalForm.id;
      if (!id) return;
      const isReject = this.approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.REJECTED;
      const note = this.approvalForm.note || '';
      if (isReject && !note.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do không duyệt!');
        return;
      }
      ApiService.patch(`v2/budget-line-requests/${id}/change-status`, {
        status: isReject ? WORKFLOW_APPROVAL_STEP_STATUS.REJECTED : WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        note,
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Cập nhật trạng thái duyệt thành công!');
          this.fetchData(this.page);
          this.$refs.approvalModal.hide();
        })
        .catch((err) => handleError(err));
    },
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatPeriodColumn(item) {
      // Ưu tiên hiển thị multi-period; fallback về cột `period` legacy
      const periods = Array.isArray(item.periods) ? item.periods : [];
      if (periods.length === 0) return this.formatPeriod(item.period);
      const sorted = [...periods].sort(
        (a, b) => a.year * 12 + a.month - (b.year * 12 + b.month),
      );
      const fmt = (p) => `${String(p.month).padStart(2, '0')}/${p.year}`;
      if (sorted.length === 1) return fmt(sorted[0]);
      return `${fmt(sorted[0])} → ${fmt(sorted[sorted.length - 1])}`;
    },
    getTotalAmount(item) {
      if (item && item.totalAmount != null) return Number(item.totalAmount) || 0;
      if (Array.isArray(item && item.periods)) {
        return item.periods.reduce(
          (s, p) => s + (Number(p.amount) || 0),
          0,
        );
      }
      // Fallback cuối: dữ liệu cũ pre-migration vẫn dùng requestedAmount
      return Number(item && item.requestedAmount) || 0;
    },
    formatDate(val, format = 'DD/MM/YYYY') {
      return val ? moment(val).format(format) : '';
    },
    statusVariant(val) {
      return BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_LINE_REQUEST_STATUS_NAME[val] || '';
    },
    fetchFilterOptions() {
      const q = this.$route?.query || {};
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);

      ApiService.query('departments/all').then(({ data }) => {
        const rows = data.data || [];
        this.departmentDropdownOptions = rows.map((d) => ({ id: d.id, name: d.name }));
        if (!this.filterDepartmentId.length) {
          const ids = toIds(q.departmentId);
          this.filterDepartmentId = this.departmentDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      }).catch(() => {});

      ApiService.get('v2/cost-items/all').then(({ data }) => {
        const rows = data.data || [];
        this.costItemDropdownOptions = rows.map((c) => ({ id: c.id, name: c.name }));
        if (!this.filterCostItemId.length) {
          const ids = toIds(q.costItemId);
          this.filterCostItemId = this.costItemDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      }).catch(() => {});

      ApiService.get('/company').then(({ data }) => {
        const rows = data.data || [];
        this.companyOptions = [
          { value: null, text: '-- Doanh nghiệp --' },
          ...rows.map((c) => ({ value: c.id, text: c.name })),
        ];
      }).catch(() => {});

      ApiService.get('v2/cost-groups/all').then(({ data }) => {
        const rows = data.data || [];
        this.costGroupDropdownOptions = rows.map((g) => ({ id: g.id, name: g.name }));
        if (!this.filterCostGroupId.length) {
          const ids = toIds(q.costGroupId);
          this.filterCostGroupId = this.costGroupDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      }).catch(() => {});

      ApiService.query('stores/getStores', { params: { byEmployeeGroup: true } }).then(({ data }) => {
        const rows = data.data || [];
        this.storeDropdownOptions = rows.map((s) => ({
          id: s.id,
          name: s.shortName ? `${s.name} (${s.shortName})` : s.name,
        }));
        if (!this.filterStoreId.length) {
          const ids = toIds(q.storeId);
          this.filterStoreId = this.storeDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      }).catch(() => {});
    },
    fetchEmployees() {
      ApiService.get('/employees/get-list-filter').then((response) => {
        const employees = response?.data?.data || [];
        this.employeeSource = employees.map((e) => {
          let suggestionName = e.fullName || e.name || '';
          if (e.code) suggestionName += ` (${e.code})`;
          const rawName = e.fullName || e.name || '';
          return {
            id: e.id,
            name: rawName,
            code: e.code || '',
            suggestionName,
            nameNoneSign: xoa_dau(rawName),
          };
        });
        this.employeeSuggestions = [...this.employeeSource];
        if (this.filterCreatedBy) {
          const found = this.employeeSource.find((e) => Number(e.id) === Number(this.filterCreatedBy));
          if (found) this.employeeModel = found.suggestionName;
        }
      }).catch(() => {});
    },
    onSelectedEmployee(option) {
      const item = option?.item || null;
      this.filterCreatedBy = item?.id || null;
      this.employeeModel = item ? item.suggestionName : '';
      this.employeeSuggestions = [...this.employeeSource];
    },
    onSearchEmployeeChange(text) {
      const t = text != null ? String(text) : '';
      this.employeeModel = t;
      if (!t.trim()) {
        this.filterCreatedBy = null;
        this.employeeSuggestions = [...this.employeeSource];
        return;
      }
      const tl = t.toLowerCase();
      this.employeeSuggestions = (this.employeeSource || [])
        .filter((item) => {
          return (
            (item.nameNoneSign || '').toLowerCase().indexOf(tl) > -1 ||
            (item.name || '').toLowerCase().indexOf(tl) > -1 ||
            (item.code || '').toLowerCase().indexOf(tl) > -1
          );
        })
        .slice(0, this.suggestLimit);
    },
    onFilter() {
      this.$router
        .push({
          name: 'list-budget-line-requests',
          query: { ...this.paginationLinkParams, page: 1 },
        })
        .catch(() => {})
        .finally(() => this.fetchData(1));
    },
    fetchData(pageNum) {
      this.page = pageNum || 1;
      this.onLoading = true;
      const q = this.$route.query || {};
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);
      const params = {
        page: this.page,
        limit: 20,
        companyId: this.filterCompanyId || undefined,
        departmentId: this.filterDepartmentId.length
          ? this.filterDepartmentId.map((d) => d.id)
          : toIds(q.departmentId).length ? toIds(q.departmentId) : undefined,
        costItemId: this.filterCostItemId.length
          ? this.filterCostItemId.map((c) => c.id)
          : toIds(q.costItemId).length ? toIds(q.costItemId) : undefined,
        storeId: this.filterStoreId.length
          ? this.filterStoreId.map((s) => s.id)
          : toIds(q.storeId).length ? toIds(q.storeId) : undefined,
        costGroupId: this.filterCostGroupId.length
          ? this.filterCostGroupId.map((g) => g.id)
          : toIds(q.costGroupId).length ? toIds(q.costGroupId) : undefined,
        status: this.filterStatus !== null ? this.filterStatus : undefined,
        createdBy: this.filterCreatedBy || undefined,
        myApproval: this.filterMyApproval ? 1 : undefined,
        sortBy: this.filterSortBy || undefined,
      };
      if (this.filterPeriod) {
        params.period = moment(this.filterPeriod, ['MM/YYYY', 'YYYY-MM-DD', 'YYYY-MM'], true).isValid()
          ? moment(this.filterPeriod, ['MM/YYYY', 'YYYY-MM-DD', 'YYYY-MM']).startOf('month').format('YYYY-MM-DD')
          : undefined;
      }
      ApiService.query('v2/budget-line-requests', { params })
        .then(({ data }) => {
          this.items = (data.data.dataset || []).map((item) => ({
            ...item,
            selected: false,
          }));
          this.totalRow = data.data.totalRow || 0;
          this.numberOfPage = data.data.totalPage || 1;
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.onLoading = false;
        });
    },
    createRequest() {
      this.$router.push({ name: 'upsert-budget-line-request' });
    },
    viewDetail(item) {
      this.$router.push({
        name: 'detail-budget-line-request',
        query: { id: item.id },
      });
    },
    detailHref(item) {
      return this.$router.resolve({
        name: 'detail-budget-line-request',
        query: { id: item.id },
      }).href;
    },
    onIdClick(event, item) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      this.$refs.quickViewModal.open(item.id);
    },

    // ===== Duyệt hàng loạt =====
    /** Phiếu đủ điều kiện chọn để duyệt: đang ở bước duyệt của user */
    isSelectable(item) {
      return Boolean(item && item.canApprove && this.isApprovable(item.status));
    },
    toggleSelectAll(checked) {
      this.selectableItems.forEach((item) => {
        item.selected = checked;
      });
    },
    clearSelection() {
      this.items.forEach((item) => {
        if (item.selected) item.selected = false;
      });
    },
    openBulkApprove() {
      const eligible = this.selectedItems.filter((item) => this.isSelectable(item));
      if (!eligible.length) {
        this.makeToastFaile('Không có phiếu nào phù hợp để duyệt.');
        return;
      }
      this.bulkForm = { note: '', eligible };
      this.$refs.bulkApproveModal.show();
    },
    /** Gửi danh sách id lên BE, mỗi phiếu xử lý độc lập */
    confirmBulkApprove() {
      const items = this.bulkForm.eligible.map((item) => ({ id: item.id }));
      this.bulkSubmitting = true;
      ApiService.post('v2/budget-line-requests/bulk-change-status', {
        items,
        status: WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        note: this.bulkForm.note || '',
      })
        .then(({ data }) => {
          this.$refs.bulkApproveModal.hide();
          const payload = data.data || {};
          this.$refs.bulkResultModal.open({
            actionLabel: 'Duyệt hàng loạt',
            results: payload.results || [],
            skipped: [],
          });
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.bulkSubmitting = false;
        });
    },
    /** Đóng modal kết quả → refresh danh sách (fetchData gán lại selected=false) */
    onBulkResultClosed() {
      this.fetchData(this.page);
    },
    editItem(item) {
      this.$router.push({
        name: 'upsert-budget-line-request',
        query: { id: item.id },
      });
    },
    showHistory(item) {
      if (!item || !item.id) return;
      ApiService.get(`v2/budget-line-requests/${item.id}`)
        .then(({ data }) => {
          const result = data.data || {};
          this.historyItems = (result.history || []).map((h) => ({
            ...h,
            actionLabel: HISTORY_ACTION_TYPE_CODE[h.action] || String(h.action || ''),
          }));
          this.$nextTick(() => {
            this.$refs.historyModal?.open();
          });
        })
        .catch((err) => handleError(err));
    },
    openApprovalWorkflow(item) {
      if (!item || !item.id) return;
      this.approvalModalRequestId = item.id;
      this.$nextTick(() => {
        this.$refs.requestApprovalModal?.open();
      });
    },
    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm('Bạn có chắc muốn xóa yêu cầu này?', {
          title: 'Xác nhận xóa',
          okVariant: 'danger',
          okTitle: 'Xóa',
          cancelTitle: 'Đóng',
        })
        .then((confirmed) => {
          if (!confirmed) return;
          ApiService.delete(`v2/budget-line-requests/${item.id}`)
            .then(({ data }) => {
              this.makeToastSuccess(data.message || 'Đã xóa thành công');
              this.fetchData(this.page);
            })
            .catch((err) => handleError(err));
        });
    },
  },
};
</script>

<style scoped>
</style>
